import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { MoreHorizontal, Monitor, Smartphone, Plus, ArrowLeftRight, X, UserPlus, QrCode } from 'lucide-react'
import { toneClass } from '@/components/ui/kpi-tile'
import { useViewportMode, useViewportOverride, setViewportOverride } from '@/hooks/useViewportMode'
import { InstallAppCard } from '@/components/InstallAppCard'
import { cn } from '@/lib/utils'
import { PoweredBy, ShopTrackLogo } from '@/components/ui/logo'
import { NotificationBell } from '@/components/NotificationBell'
import { NppNotificationBell } from '@/components/announce/NppNotificationBell'
import { WORK_MODE_LABEL, setWorkMode } from '@/lib/workMode'
import { ModeChooser, ModeSwitch } from './ModeSwitch'
import { NAV_META, isWideShellPath, useShellModel, type NavItem } from './shell-model'
import { useModeSlide } from './useModeSlide'
import { WideShellContext, WideShellFallbackHeader, type WideShell } from './wide-shell'
import { UnitSwitcher } from './UnitSwitcher'
import { UnitAvatar } from '@/components/settings/ShopAvatar'
import { ShopReferralDialog } from '@/components/settings/ShopReferralCard'
import { PricingBanner, isUnitType } from '@/components/pricing/PricingNotice'
import { SeasonDeco, SeasonDockCap, SeasonFlag, SeasonHat, SeasonLayer } from '@/components/season/Season'

// ── Sidebar / danh sách menu dùng chung cho desktop và sheet "Thêm" ──
/** Số đếm (vd tin chưa đọc) cho một mục. Gom Đơn: số hội thoại chưa đọc trên mục "Gom Đơn" (useGomdonNavBadge). */
export type NavBadgeFor = (to: string) => number | null | undefined

export function NavList({
  items,
  activePath,
  onNavigate,
  dense = false,
  badgeFor,
}: {
  items: NavItem[]
  activePath: string | null
  onNavigate?: () => void
  /** Sheet mobile: ô chạm cao hơn. */
  dense?: boolean
  badgeFor?: NavBadgeFor
}) {
  return (
    <nav className="space-y-px">
      {items.map((item, idx) => {
        const prev = items[idx - 1]
        const showGroup = item.group && (!prev || prev.group !== item.group)
        const isActive = activePath === item.to
        const meta = NAV_META[item.to]
        const badge = badgeFor?.(item.to)
        return (
          <div key={`${item.to}-${idx}`}>
            {showGroup && (
              <p className={cn('px-2 pb-1 text-xs font-medium text-muted', idx === 0 ? 'pt-1' : 'pt-4')}>
                {item.group}
              </p>
            )}
            <Link
              to={item.to}
              onClick={onNavigate}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'motion-state flex items-center gap-2.5 rounded-lg px-2 text-sm',
                dense ? 'min-h-[48px] py-2' : 'py-1.5',
                // Mục con thẳng hàng với chữ của mục cha (bỏ qua ô icon 28px + khoảng 10px).
                item.child && 'pl-[46px]',
                isActive
                  ? 'bg-accent/10 font-semibold text-accent'
                  : 'text-inkSoft hover:bg-paperSoft hover:text-ink',
              )}
            >
              {!item.child && meta && (
                <span
                  className={cn(
                    'grid size-7 shrink-0 place-items-center rounded-lg',
                    isActive ? 'bg-accent text-onAccent' : cn(toneClass(meta.tone), 'bg-toneBg text-toneFg'),
                  )}
                  aria-hidden
                >
                  <meta.icon className="size-4" />
                </span>
              )}
              <span className="min-w-0 truncate">{item.label}</span>
              {!!badge && badge > 0 && (
                <span className="ml-auto min-w-5 shrink-0 rounded-full bg-accent px-1.5 text-center text-xs font-semibold leading-5 text-onAccent">
                  {badge > 99 ? '99+' : badge}
                  <span className="sr-only"> chưa đọc</span>
                </span>
              )}
            </Link>
          </div>
        )
      })}
    </nav>
  )
}

/**
 * Lối thoát khỏi việc tự động nhận diện.
 *
 * Bắt buộc phải có: quy tắc mới coi mọi màn cảm ứng rộng ≤1366px là mobile, mà
 * LAPTOP MÀN CẢM ỨNG cũng khớp điều kiện đó. Không có nút này thì người dùng
 * laptop cảm ứng kẹt trong giao diện điện thoại và không có đường ra.
 *
 * Chỉ hiện khi có việc để làm: trên máy desktop thật (không cảm ứng, màn rộng)
 * thì không có gì để đổi nên nút không xuất hiện.
 */
export function ViewportToggle({ onDone }: { onDone?: () => void }) {
  const mode = useViewportMode()
  const override = useViewportOverride()

  if (mode === 'desktop' && !override) return null

  const toDesktop = mode === 'mobile'
  return (
    <button
      onClick={() => {
        // Quay lại mobile = bỏ lựa chọn thủ công, trả về tự động nhận diện.
        setViewportOverride(toDesktop ? 'desktop' : null)
        onDone?.()
      }}
      className="motion-state flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-inkSoft hover:bg-paperSoft hover:text-ink"
    >
      {toDesktop ? <Monitor className="size-4 shrink-0" /> : <Smartphone className="size-4 shrink-0" />}
      {toDesktop ? 'Xem bản máy tính' : 'Xem bản điện thoại'}
    </button>
  )
}

/** Lối đổi qua lại giữa khu Quản trị NPP và đơn vị của tài khoản (người có cả hai). */
export function CrossLink({ to, label, onNavigate, dense = false }: { to: string; label: string; onNavigate?: () => void; dense?: boolean }) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={cn(
        'motion-state mt-3 flex items-center gap-2.5 rounded-lg border border-dashed border-rule px-2 text-sm font-medium text-accent hover:bg-accent/5',
        dense ? 'min-h-[48px] py-2' : 'py-1.5',
      )}
    >
      <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-accent/10" aria-hidden>
        <ArrowLeftRight className="size-4" />
      </span>
      {label}
    </Link>
  )
}

function AccountLine({ name, level, avatarUrl }: { name: string; level: string; avatarUrl?: string | null }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <UnitAvatar name={name} src={avatarUrl} className="flex size-9 items-center justify-center rounded-full bg-accent text-sm font-semibold text-onAccent">
        <SeasonHat />
      </UnitAvatar>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-ink" title={name}>
          {name || 'Tài khoản'}
        </p>
        <p className="truncate text-xs text-muted">{level}</p>
      </div>
    </div>
  )
}

type ShellModel = ReturnType<typeof useShellModel>

/**
 * Ruột sidebar desktop — dùng cho cột trái và cho menu đè (☰) ở trang cần cả khung nhìn (isWideShellPath).
 * `onClose` có = đang là menu đè: thêm nút đóng cạnh logo, bấm mục nào cũng đóng menu (`onNavigate`).
 */
function SidebarBody({ m, onNavigate, onClose }: { m: ShellModel; onNavigate?: () => void; onClose?: () => void }) {
  const { shopName, levelInfo, currentNav, activePath, onLogout, canSwitchMode, mode, switchMode, nppMode, crossLink, units, switchUnit, auth, navBadgeFor } = m
  return (
    <>
      <div className={cn('flex h-14 items-center border-b border-rule px-5', onClose && 'justify-between gap-2 pr-2')}>
        <Link to="/" onClick={onNavigate} className="motion-state flex items-center text-accent hover:opacity-80">
          <ShopTrackLogo className="text-accent" textClassName="text-lg text-accent" />
        </Link>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Đóng menu"
            className="motion-state grid size-10 shrink-0 place-items-center rounded-lg text-inkSoft hover:bg-paperSoft hover:text-ink"
          >
            <X className="size-[18px]" aria-hidden />
          </button>
        )}
      </div>

      {canSwitchMode && (
        <div className="border-b border-rule px-3 py-3">
          <ModeSwitch mode={mode} onChange={switchMode} className="w-full" />
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-3">
        <NavList items={currentNav} activePath={activePath} onNavigate={onNavigate} badgeFor={navBadgeFor} />
        {crossLink && <CrossLink to={crossLink.to} label={crossLink.label} onNavigate={onNavigate} />}
      </div>

      <div className="space-y-3 border-t border-rule p-3">
        <AccountLine name={shopName} level={levelInfo.levelTag} avatarUrl={m.avatarUrl} />
        <UnitSwitcher units={units} activeId={auth.activeShopId} onSwitch={switchUnit} />
        {/* Sidebar chỉ hiện ở chế độ desktop, nên nút này ở đây chỉ xuất hiện
            khi người dùng đã tự chọn desktop trên một máy cảm ứng — tức là
            đường quay về bản điện thoại. */}
        <ViewportToggle onDone={onNavigate} />
        <div className="flex items-center justify-between px-0.5 text-xs">
          {nppMode ? <span /> : (
            <Link to="/feedback" onClick={onNavigate} className="motion-state text-muted hover:text-ink">
              Góp ý
            </Link>
          )}
          <button onClick={onLogout} className="motion-state text-muted hover:text-danger">
            Đăng xuất
          </button>
        </div>
        <PoweredBy className="px-0.5" />
      </div>
    </>
  )
}

export default function DashboardLayout() {
  const [moreOpen, setMoreOpen] = useState(false)
  const model = useShellModel()
  const {
    shopName, levelInfo, roleTheme, currentNav, phoneTabs, primaryAction, addUnitTo,
    activePath, activePhone, currentLabel, onLogout, navBadgeFor,
    canSwitchMode, mode, modeLabel, modeChosen, switchMode, tenantType,
    nppMode, crossLink, units, switchUnit, auth, canPeerRefer, referLabel, referKind,
  } = model
  const slideRef = useModeSlide<HTMLDivElement>(mode)
  // CTV Bán hàng: mã QR giới thiệu shop tự do (nút trước Tạo đơn ở thanh trên). Shop trên máy tính giữ thẻ ở Cài đặt.
  const referDesk = canPeerRefer && referKind === 'free'
  const [referOpen, setReferOpen] = useState(false)
  const otherMode = mode === 'manage' ? 'sell' : 'manage'

  // Trang cần cả khung nhìn (sơ đồ mạng lưới cây ngang) trên MÁY TÍNH: ẩn cột menu trái, nội dung hết bề ngang và cao
  // đúng một màn (trang tự cuộn bên trong, không cuộn cả trang); ☰ ở thanh trên mở menu đè. Điện thoại giữ nguyên —
  // quyết định theo useViewportMode (cảm ứng), không theo bề rộng.
  const location = useLocation()
  const viewport = useViewportMode()
  const wide = viewport === 'desktop' && isWideShellPath(location.pathname)
  const [menuOpen, setMenuOpen] = useState(false)
  // Menu đè luôn đóng khi vào trang / chuyển trang — không nhớ
  useEffect(() => setMenuOpen(false), [location.pathname])
  // ☰ + phần cuối thanh trên (cờ theo dịp, chuông) đưa xuống hàng đầu của trang
  const bell = nppMode ? <NppNotificationBell /> : <NotificationBell />
  // Trang có vẽ hàng đầu (WidePageHeader) chưa — đếm số hàng đầu đang gắn
  const headerCount = useRef(0)
  const [hasHeader, setHasHeader] = useState(false)
  const registerHeader = useCallback(() => {
    headerCount.current += 1
    setHasHeader(true)
    return () => {
      headerCount.current -= 1
      if (headerCount.current === 0) setHasHeader(false)
    }
  }, [])
  const wideShell: WideShell = {
    openMenu: () => setMenuOpen(true),
    menuOpen,
    registerHeader,
    trailing: (
      <>
        <SeasonFlag />
        {bell}
      </>
    ),
  }

  return (
    <div
      data-role-theme={roleTheme}
      className={wide ? 'min-h-dvh grid grid-cols-1 bg-paper' : 'min-h-dvh grid grid-cols-1 bg-paper desktop:grid-cols-[236px_minmax(0,1fr)]'}
    >
      <SeasonLayer />
      {/* ── Sidebar desktop ─────────────────────────────────────── */}
      {!wide && (
        <aside data-shell="side" className="sticky top-0 hidden h-dvh flex-col border-r border-rule bg-raise desktop:flex">
          <SidebarBody m={model} />
        </aside>
      )}

      {/* ── Menu đè (☰) ở trang cần cả khung nhìn ─────────────────── */}
      {wide && (
        <DialogPrimitive.Root open={menuOpen} onOpenChange={setMenuOpen}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink/30 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
            {/* data-shell="side": nhận màu menu thương hiệu NPP (index.css) và nền theo dịp (season.css) như cột menu */}
            <DialogPrimitive.Content
              aria-describedby={undefined}
              data-shell="side"
              data-role-theme={roleTheme}
              className="fixed inset-y-0 left-0 z-50 flex w-[264px] max-w-[85vw] flex-col border-r border-rule bg-raise shadow-overlay data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:animate-in data-[state=open]:slide-in-from-left"
            >
              <DialogPrimitive.Title className="sr-only">Menu</DialogPrimitive.Title>
              <SidebarBody m={model} onNavigate={() => setMenuOpen(false)} onClose={() => setMenuOpen(false)} />
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      )}

      {/* ── Nội dung ────────────────────────────────────────────── */}
      <main className={wide ? 'flex h-dvh min-h-0 min-w-0 flex-col overflow-hidden' : 'flex min-h-dvh min-w-0 flex-col'}>
        {/* Trang cần cả khung nhìn: không có thanh trên — trang tự vẽ hàng đầu (WidePageHeader: ☰ · tiêu đề · nút ·
            chuông, components/layout/wide-shell.tsx) */}
        {!wide && (
        <header data-shell="top" className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-rule bg-raise px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-2.5">
            {/* Logo cao 28px nhưng vùng chạm phải đủ 44px. Thanh đầu cao 56px
                nên nới vùng chạm không làm thanh cao thêm. */}
            <Link to="/" aria-label="Về trang tổng quan"
              className="motion-state -mx-1 flex min-h-11 shrink-0 items-center px-1 hover:opacity-80 desktop:min-h-0 desktop:hidden">
              <ShopTrackLogo className="text-accent" showText={false} />
            </Link>
            {canSwitchMode ? (
              <ModeSwitch mode={mode} onChange={switchMode} size="sm" className="desktop:hidden" />
            ) : (
              <span className="truncate text-sm font-medium text-ink desktop:hidden">{currentLabel}</span>
            )}
            {canSwitchMode ? (
              // Luôn biết đang ở chế độ nào: "Bán hàng › Đơn hàng"
              <span className="hidden truncate text-sm font-medium text-ink desktop:inline">
                <span className="text-muted">{modeLabel} ›</span> {currentLabel}
              </span>
            ) : (
              <span className="hidden truncate text-sm font-medium text-ink desktop:inline" title={shopName}>
                {shopName}
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <SeasonFlag />
            {/* Chủ CTV ở Bán hàng: biểu tượng QR ở góc, bấm hiện mã QR to (docs/KE_HOACH_SHOP_GIOI_THIEU.md §9) */}
            {referDesk && (
              <button
                type="button"
                onClick={() => setReferOpen(true)}
                aria-haspopup="dialog"
                aria-label={referLabel}
                title={referLabel}
                className="motion-state flex size-11 items-center justify-center rounded-lg text-inkSoft hover:bg-accent/5 hover:text-accent desktop:size-10"
              >
                <QrCode className="size-5" aria-hidden />
              </button>
            )}
            {/* Tạo đơn bằng Excel nằm trong trang Tạo đơn (routes/OrderCreate.tsx) */}
            {primaryAction === 'createOrder' && (
              <Link
                to="/orders/new"
                data-season-origin="desktop"
                className="motion-state inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-accent px-3 text-sm font-semibold text-onAccent hover:bg-accentSoft desktop:min-h-9"
              >
                <Plus className="size-4" aria-hidden />
                <span className="mobile:sr-only">Tạo đơn</span>
              </Link>
            )}
            {/* Chế độ Quản lý / khu NPP: cùng chỗ là Thêm tài khoản (docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md §4.1) */}
            {primaryAction === 'addUnit' && addUnitTo && (
              <Link
                to={addUnitTo}
                aria-label="Thêm tài khoản"
                className="motion-state inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-lg bg-accent px-3 text-sm font-semibold text-onAccent hover:bg-accentSoft desktop:min-h-9"
              >
                <UserPlus className="size-4" aria-hidden />
                <span className="mobile:sr-only">Thêm tài khoản</span>
              </Link>
            )}
            {nppMode ? <NppNotificationBell /> : <NotificationBell />}
          </div>
        </header>
        )}

        {/* Đệm đáy phải theo CHẾ ĐỘ, không theo bề rộng. Bản cũ là
            `pb-28 … lg:pb-8 xl:p-8`: ở 1366px thì `xl:p-8` đặt lại đệm đáy
            còn 2rem, trước đây vô hại vì 1366px luôn là desktop nên không có
            thanh tab đáy. Nay iPad Pro ngang (1366px) là MOBILE và có thanh
            tab, nên nếu giữ nguyên thì nội dung cuối trang chui xuống dưới
            thanh tab. `mobile:pb-28` có độ ưu tiên cao hơn `xl:p-8` nên thắng
            bất kể thứ tự. */}
        {/* Lồng đèn / đèn nháy theo dịp treo ngay dưới thanh trên nhưng thuộc trang và nằm SAU nội dung
            (isolate + -z-10): cuộn thì trôi theo trang, chui dưới thanh trên — không đè lên thẻ, nút. Trước
            đây gắn trong thanh trên (sticky, z-30) nên luôn nổi trên mọi thứ đang cuộn qua. */}
        <div className={wide ? 'relative isolate flex min-h-0 flex-1 flex-col' : 'relative isolate flex flex-1 flex-col'}>
          <div className="absolute inset-x-0 top-0 -z-10">
            <SeasonDeco variant="desktop" />
          </div>
          {/* Cả khung nhìn: trang (flex-1 min-h-0) tự chia chiều cao; trang chưa theo kiểu đó vẫn cuộn được ở đây */}
          <div
            ref={slideRef}
            className={wide
              ? 'app-content flex min-h-0 w-full flex-1 flex-col overflow-y-auto px-6 pb-6 pt-5'
              : 'app-content mx-auto w-full max-w-[1600px] flex-1 p-4 sm:p-6 xl:p-8 mobile:pb-28 desktop:pb-8'}
          >
            <WideShellContext.Provider value={wide ? wideShell : null}>
              {wide && !hasHeader && <WideShellFallbackHeader shell={wideShell} />}
              {/* Đơn vị chưa có bảng giá / chiết khấu (docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md §4.5) */}
              <PricingBanner enabled={!nppMode && isUnitType(tenantType)} className={wide ? 'mb-3.5 shrink-0' : 'mb-4'} />
              <Outlet />
            </WideShellContext.Provider>
          </div>
        </div>
      </main>

      {/* ── Thanh tab dưới đáy (mobile) ─────────────────────────── */}
      <nav data-shell="dock" className="fixed inset-x-0 bottom-0 z-40 border-t border-rule bg-raise desktop:hidden">
        <SeasonDockCap />
        <div className="flex h-16 items-stretch">
          {phoneTabs.map((item) => {
            const isActive = activePhone === item.to
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'motion-state relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1',
                  isActive ? 'text-accent' : 'text-muted',
                )}
              >
                <item.Icon className="size-5 shrink-0" />
                <span className="max-w-full truncate text-[10px] font-medium tracking-tight">{item.label}</span>
                {isActive && <span className="absolute bottom-0 h-0.5 w-8 rounded-full bg-accent" />}
              </Link>
            )
          })}
          <button
            onClick={() => setMoreOpen(true)}
            aria-current={activePhone === 'more' ? 'page' : undefined}
            className={cn(
              'motion-state relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1',
              activePhone === 'more' ? 'text-accent' : 'text-muted',
            )}
          >
            <MoreHorizontal className="size-5 shrink-0" />
            <span className="text-[10px] font-medium tracking-tight">Thêm</span>
            {activePhone === 'more' && <span className="absolute bottom-0 h-0.5 w-8 rounded-full bg-accent" />}
          </button>
        </div>
        <div className="bg-raise pb-safe" />
      </nav>

      {/* ── Sheet "Thêm" ────────────────────────────────────────── */}
      {/* Trượt lên từ đáy, cùng hướng với nút mở nó. Bản cũ mở drawer từ mép
          TRÁI trong khi nút "Thêm" nằm dưới đáy — hai hướng ngược nhau. */}
      <DialogPrimitive.Root open={moreOpen} onOpenChange={setMoreOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[85dvh] overflow-y-auto rounded-t-2xl border-t border-rule bg-raise px-3 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 shadow-overlay data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom">
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-rule" />
            <DialogPrimitive.Title asChild>
              <div className="px-2 pb-3">
                <AccountLine name={shopName} level={levelInfo.levelTag} avatarUrl={model.avatarUrl} />
              </div>
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">
              Toàn bộ mục điều hướng của tài khoản
            </DialogPrimitive.Description>

            <div className="border-t border-rule pt-2">
              {canSwitchMode && <p className="px-2 pb-1 text-xs font-medium text-muted">{modeLabel}</p>}
              <NavList items={currentNav} activePath={activePath} onNavigate={() => setMoreOpen(false)} dense badgeFor={navBadgeFor} />
            </div>

            {crossLink && (
              <div className="mt-2 border-t border-rule pt-2">
                <CrossLink to={crossLink.to} label={crossLink.label} onNavigate={() => setMoreOpen(false)} dense />
              </div>
            )}

            {units.length > 1 && (
              <div className="mt-2 border-t border-rule pt-2">
                <UnitSwitcher units={units} activeId={auth.activeShopId} onSwitch={async (id) => { await switchUnit(id); setMoreOpen(false) }} dense />
              </div>
            )}

            {canSwitchMode && (
              <div className="mt-2 border-t border-rule pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setMoreOpen(false)
                    switchMode(otherMode)
                  }}
                  className="motion-state flex min-h-[48px] w-full items-center gap-2.5 rounded-lg px-2 text-left text-sm font-medium text-accent hover:bg-accent/5"
                >
                  <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-accent/10" aria-hidden>
                    <ArrowLeftRight className="size-4" />
                  </span>
                  Chuyển sang {WORK_MODE_LABEL[otherMode]}
                </button>
              </div>
            )}

            <div className="mt-2 border-t border-rule pt-2">
              <ViewportToggle onDone={() => setMoreOpen(false)} />
            </div>

            {/* Mời cài đặt nằm trong sheet "Thêm" chứ không chen lên đầu màn:
                đây là việc làm một lần, không đáng chiếm chỗ của công việc
                hằng ngày. Thẻ tự ẩn khi đã cài hoặc người dùng đã tắt. */}
            <div className="mt-2 px-1">
              <InstallAppCard />
            </div>

            <div className="mt-2 flex items-center justify-between border-t border-rule px-3 pt-3 text-sm">
              {nppMode ? <span /> : (
                <Link to="/feedback" onClick={() => setMoreOpen(false)} className="motion-state py-2 text-muted hover:text-ink">
                  Góp ý
                </Link>
              )}
              <button onClick={onLogout} className="motion-state py-2 text-muted hover:text-danger">
                Đăng xuất
              </button>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      {referDesk && <ShopReferralDialog open={referOpen} onOpenChange={setReferOpen} title={referLabel} kind={referKind} />}

      <ModeChooser
        open={canSwitchMode && !modeChosen}
        isAgency={tenantType === 'agency'}
        onPick={switchMode}
        onDismiss={() => setWorkMode(mode, true)}
      />
    </div>
  )
}
